import { useEffect, useRef, useState } from "react";
import { t } from "../i18n";
import { useModalKeys } from "./Modal";
import { Link } from "./Nav";

// "How the item bank works" (byj-1wv.51.18): a button on /bank's head that
// opens, as the Exams pages' guide does (components/ExamGuide), the bank as
// a whole on one page, read once: the flow from an item to a class's
// released grade, each step the bank's or Share's; how a paper ties to its
// class and its exam; how a bank paper differs from an exam one sets
// oneself. What to do next is the home's, not this.

/** Where a step of the whole flow happens: the bank, or Share's own pages. */
type Place = "bank" | "share";
const flow: { key: "item" | "blueprint" | "paper" | "exam" | "assign" | "sit" | "release"; place: Place }[] = [
  { key: "item", place: "bank" },
  { key: "blueprint", place: "bank" },
  { key: "paper", place: "bank" },
  { key: "exam", place: "share" },
  { key: "assign", place: "share" },
  { key: "sit", place: "bank" },
  { key: "release", place: "share" },
];

function Overview() {
  const rows = ["write", "answers", "grading", "classes", "edit"] as const;
  return (
    <div className="stack" data-testid="bank-guide-overview">
      <ol className="bank-map" aria-label={t("bank:guideFlowLabel")}>
        {flow.map(({ key, place }, i) => (
          <li key={key} className={`bank-map-step is-${place}`}>
            <span className="bank-map-head">
              <span className="stage-mark" aria-hidden="true">
                {i + 1}
              </span>
              <strong>{t(`bank:guideFlow.${key}.title`)}</strong>
              <span className="chip bank-map-place">{t(`bank:guidePlace.${place}`)}</span>
            </span>
            <span className="muted small">{t(`bank:guideFlow.${key}.text`)}</span>
          </li>
        ))}
      </ol>
      <section className="stack tight">
        <h4 className="flush">{t("bank:guideTiesTitle")}</h4>
        <ul className="small bank-check">
          {(["exam", "class", "answers", "grades", "measure"] as const).map((k) => (
            <li key={k}>{t(`bank:guideTies.${k}`)}</li>
          ))}
        </ul>
      </section>
      <section className="stack tight">
        <h4 className="flush">{t("bank:guideCompareTitle")}</h4>
        <table className="bank-compare small">
          <thead>
            <tr>
              <th scope="col" />
              <th scope="col">{t("bank:guideCompareOwn")}</th>
              <th scope="col">{t("bank:guideCompareBank")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r}>
                <th scope="row">{t(`bank:guideCompare.${r}.what`)}</th>
                <td>{t(`bank:guideCompare.${r}.own`)}</td>
                <td>{t(`bank:guideCompare.${r}.bank`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

export function BankGuideButton() {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  return (
    <>
      <button ref={button} className="small" aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? "bank-guide" : undefined} onClick={() => setOpen(true)}>
        {t("bank:guideButton")}
      </button>
      {open && <BankGuideDialog onClose={close} />}
    </>
  );
}

function BankGuideDialog({ onClose }: { onClose: () => void }) {
  const keys = useModalKeys(onClose);
  const closeButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeButton.current?.focus();
  }, []);
  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div id="bank-guide" className="modal guide-modal stack" role="dialog" aria-modal="true" aria-labelledby="bank-guide-title" onKeyDown={keys} data-testid="bank-guide">
        <div className="row">
          <h3 id="bank-guide-title" className="grow flush">
            {t("bank:guideButton")}
          </h3>
          <button ref={closeButton} type="button" className="small" aria-label={t("common:close")} onClick={onClose}>
            ✕
          </button>
        </div>
        <p className="muted small flush">{t("bank:guideLead")}</p>
        <Overview />
        <div className="row modal-actions">
          <Link to="/exams" className="button">
            {t("bank:guideToExams")}
          </Link>
        </div>
      </div>
    </div>
  );
}
